<template>
    <div class="container" v-if="!displayModalChoixSite">
        <div class="title-container">
            <span>
                {{ $t('rapports.signer.header') }}
                <ReglePastille cle="rapportsASigner.definition" />
            </span>
        </div>
        <div class="content-container">
            <div class="filter-container">
                <div class="filter-site-container" v-if="!isUniqueSite">
                    {{$t('intervention.non-preventive.filter')}}
                    <ReglePastille cle="rapportsASigner.filtreSiteUnique" />
                    <ButtonBlue
                        :on-click="selectionnerSite"
                        :text="$t('bouton.choix.site')"
                        class="button-confirm btn"
                        :disabled="tableData.length === 0 && isLoading"
                    />
                </div>
                <Filter
                    @search="search"
                    @setInterventionId="this.setInterventionId"
                    @setPredictedDateFrom="this.setPredictedDateFrom"
                    @setPredictedDateTo="this.setPredictedDateTo"
                    @clearFilter="this.clearFilter"
                    :filter="this.filter"
                    :searchSite="this.searchSite"
                />
            </div>
            <div class="dataTable">
                <div class="container-header-table">
                    <h2>
                        {{ this.count }} {{ $t('rapports.signer.table.header') }}
                        <ReglePastille cle="rapportsASigner.portee" />
                        <ReglePastille cle="rapportsASigner.selectionPourSignature" />
                    </h2>
                    <BorderedIconButton v-if="!isExportDisabled" @click="exportXls" :imgUrl="iconeXls" :title="$t('list.export')"/>
                    <BorderedIconButton v-if="!isSignerActivated"
                                        @click="signerRapports"
                                        :imgUrl="iconeSignature" :title="$t('rapports.signer.bouton')" />
                    <ReglePastille cle="rapportsASigner.signerActif" />
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" @scroll="handleScroll" class="datatable-container">
                    <DataTable
                        :count="+count"
                        :isLoading="isLoading"
                        :tableData="this.tableData"
                        :tableHeader="this.tableHeader"
                        :stop-propa-on-pdf="true"
                        :selected-items="listRapportsIdSelected"
                        :document-list-to-download="listRapportsIdSelected"
                        @sort="doSort"
                        @select="selectRapport"
                        @downloadPdf="downloadPdf"
                    />
                </div>
            </div>
            <WaitingModal v-if="signatureEnCours "/>
        </div>
        <ToasterSuccess v-if="isSuccessActive" :message="$t('rapports.signer.success')"/>
    </div>
    <ModalChoixSite v-else
                    @cancel="modalChoixSiteCancel"
                    @valider="validerSite"
                    :selected-site="null"
                    :selected-sites="filter.siteList"
                    :is-multi-site="true"
                    :inclure-termines="true"/>
    <WaitingModal v-if="isXlsLoading"/>
</template>

<script lang="ts">
import DataTable from '@/components/ui-components/DataTable.vue';
import { SortType, sortValue } from '@/models/SortType';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import { defineComponent, onMounted, ref } from 'vue';
import Filter from '@/modules/Common/components/Filter.vue';
import { Site } from '@/models/Site';
import { TableHeader } from '@/models/TableHeader';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import { store } from '@/plugins/store';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import { RapportASignerActions } from '@/modules/RapportASigner/store/action-types';
import { RAPPORTS_A_SIGNER } from '@/modules/RapportASigner/store/mutation-types';
import ErrorManager from '@/common/utils/ErrorManager';
import RapportASignerService from '@/modules/RapportASigner/services/RapportASignerService';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import DownloadPdf from '@/common/utils/DownloadPdf';
import ToasterSuccess from '@/components/ui-components/ToasterSuccess.vue';
import { SUCCESS } from '@/modules/SuccessToaster/store/mutation-types';
import ModalChoixSite from '@/modules/Common/components/ModalChoixSite.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Intervention {
    id: number;
    objet: string;
    type: string;
    dateDebutPrevue: string;
    adresseSite: string;
}

interface Data {
    siteList: any[];
    filter: {
        siteList: any[];
        interventionId: string;
        predictedDateFrom: string;
        predictedDateTo: string;
    };
    siteListTotal: any[];
    debounce: number;
    searchSite: string;
    listRapportsIdSelected: number[];
    signatureEnCours: boolean;
    displayModalChoixSite: boolean;
    isUniqueSite: boolean;
}

export default defineComponent({
    name: 'ListInterventionPage',
    components: {
        ToasterSuccess,
        Filter,
        DataTable,
        LoaderComponent,
        WaitingModal,
        BorderedIconButton,
        ButtonBlue,
        ModalChoixSite,
        ReglePastille,
    },
    data(): Data {
        return {
            siteList: [],
            filter: {
                siteList: [],
                interventionId: '',
                predictedDateFrom: '',
                predictedDateTo: '',
            },
            siteListTotal: [],
            debounce: 0,
            searchSite: '',
            listRapportsIdSelected: [],
            signatureEnCours: false,
            displayModalChoixSite: false,
            isUniqueSite: false,
        };
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    beforeMount() {
        this.tableData = [];
        this.filter.siteList = this.filterSite;
        SiteListService.getSiteList(new SiteSearchDTO(0, '', true, true)).then(res => {
            if (res.data.length === 1) {
                this.isUniqueSite = true;
                this.displayModalChoixSite = false;
            }
            this.siteList = res.data.map(
                ({ id, chantierId, description }: { id: string; chantierId: number; description: string }) => ({
                    id,
                    chantierId,
                    description,
                })
            );
            this.siteListTotal = this.siteList;

            this.filter.interventionId = this.filterInterventionId;
            this.filter.predictedDateFrom = this.filterPredictedDateFrom;
            this.filter.predictedDateTo = this.filterPredictedDateTo;
        });
        if (!this.displayModalChoixSite) {
            this.getInterventionData();
        }

    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
    },
    methods: {
        getInterventionData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });
            store.dispatch(`rapportASignerState/${RapportASignerActions.GET_RAPPORTS_INTERVENTIONS}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                chantierIds:
                    this.filterSite?.length === this.siteListTotal?.length
                        ? []
                        : this.filterSite?.map(x => x.chantierId),
                adresseProjetIds:
                    this.filterSite?.length === this.siteListTotal?.length
                        ? []
                        : this.filterSite?.map(x => x.id),
                idIntervention: this.filterInterventionId !== '' ? this.filterInterventionId : undefined,
                dateDebutPrevueApres:
                    this.filterPredictedDateFrom !== '' ? this.filterPredictedDateFrom : undefined,
                dateDebutPrevueAvant:
                    this.filterPredictedDateTo !== '' ? this.filterPredictedDateTo : undefined,
            });
        },
        exportXls() : void {
            store.dispatch(`rapportASignerState/${RapportASignerActions.GET_XLS_RAPPORTS_A_SIGNER}`, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                chantierIds:
                    this.filterSite?.length === this.siteListTotal?.length
                        ? []
                        : this.filterSite?.map(x => x.chantierId),
                idIntervention: this.filterInterventionId !== '' ? this.filterInterventionId : undefined,
                dateDebutPrevueApres:
                    this.filterPredictedDateFrom !== '' ? this.filterPredictedDateFrom : undefined,
                dateDebutPrevueAvant:
                    this.filterPredictedDateTo !== '' ? this.filterPredictedDateTo : undefined,
            });
        },
        signerRapports(){
            this.signatureEnCours = true;
            const idsRapports : number[] = [];
                this.listRapportsIdSelected.forEach(i => {
                    idsRapports.push(i);
                });
            RapportASignerService.signerRapports(idsRapports).then(async response => {
                if(response.status === 200){
                    this.signatureEnCours = false;
                    store.commit(`successToaster/${SUCCESS.SET_ACTIVE}`, true);
                    this.getInterventionData();
                    setTimeout(() =>store.commit(`successToaster/${SUCCESS.SET_ACTIVE}`, false), 5000);
                }
            }).catch( async error =>{
                await ErrorManager.processError(error);
                store.commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(error, 'app.error.undefined'),
                    { root: true }
                );
                this.signatureEnCours = false;
            });
        },
        search() {
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.RESET_ITEMS}`);
            this.doFilter();
        },
        doFilter() {
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.SET_FILTER_SITE}`, this.filter.siteList);
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.SET_RAPPORT_A_SIGNER_SET_FILTER_SITE}`, this.filter.siteList);
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.RAPPORT_A_SIGNER_SET_FILTER_INTERVENTION_ID}`, this.filter.interventionId);
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.SUIVI_RAPPORT_A_SIGNER_SET_FILTER_PREDICTED_DATE_FROM}`, this.filter.predictedDateFrom);
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.SUIVI_RAPPORT_A_SIGNER_SET_FILTER_PREDICTED_DATE_TO}`, this.filter.predictedDateTo);
            this.getInterventionData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.SET_SORT}`, { sortData, sortStatus });
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.RESET_ITEMS}`);
            this.getInterventionData();
        },
        setInterventionId(value: string): void {
            this.filter.interventionId = value;
        },
        setPredictedDateFrom(value: string): void {
            this.filter.predictedDateFrom = value;
        },
        setPredictedDateTo(value: string): void {
            this.filter.predictedDateTo = value;
        },
        clearFilter() {
            this.filter.siteList = this.siteListTotal ?? [];
            this.filter.interventionId = '';
            this.filter.predictedDateFrom = '';
            this.filter.predictedDateTo = '';

            this.doFilter();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.SET_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.getInterventionData(this.page + 1);
            }
        },
        selectRapport(item: { id: { pageName: string; value: number; }; }){
            const present = this.listRapportsIdSelected.find(l => l === item.id.value);
            if(present == undefined) {
                this.listRapportsIdSelected.push(item.id.value);
            } else {
                const index = this.listRapportsIdSelected.indexOf(item.id.value, 0);
                this.listRapportsIdSelected.splice(index, 1);
            }
        },
        async downloadPdf(item: any) {
            DownloadPdf.getInterventionPdf(item.id.value, 'Rapport_intervention-');
        },
        validerSite(sites: Site[]) : void {
            this.siteList = sites;
            this.displayModalChoixSite = false;
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.SET_FILTER_SITE}`, sites);
            store.commit(`rapportASignerState/${RAPPORTS_A_SIGNER.RESET_ITEMS}`);
            this.getInterventionData();
        },
        modalChoixSiteCancel(): void {
            this.siteList = this.siteListTotal;
            this.displayModalChoixSite = false;
            if (this.tableData.length === 0 && !this.isLoading) this.getInterventionData();
        },
        selectionnerSite() : void {
            this.displayModalChoixSite = true;
        }
    },
    computed: {
        tableData() {
            return store.getters['rapportASignerState/getRapportASignerItems'].map(
                ({ id, objet, dateDebutPrevue }: Intervention) => {

                    return {
                        uniqueDocumentId: id,
                        id: { pageName: 'interventionDetailView', value: id, label: id },
                        objet,
                        dateDebutPrevue: dateDebutPrevue ? new Date(dateDebutPrevue).toLocaleDateString() : '',
                        pdfExist: true
                    };
                }
            );
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return store.getters['rapportASignerState/getRapportASignerSort'];
        },
        tableHeader(): TableHeader[] {
            return store.getters['rapportASignerState/getRapportASignerTableHeader'];
        },
        page() {
            return store.getters['rapportASignerState/getRapportASignerPage'];
        },
        numberOfPages() {
            return store.getters['rapportASignerState/getRapportASignerNbOfPages'];
        },
        scroll() {
            return store.getters['rapportASignerState/getRapportsASignerScroll'];
        },
        isLoading() {
            return store.getters['rapportASignerState/getRapportASignerIsLoading'];
        },
        isXlsLoading() {
            return store.getters['rapportASignerState/getXlsIsLoading'];
        },
        filterSite(): any[] {
            return store.getters['rapportASignerState/getRapportInterventionFilterSite'];
        },
        filterInterventionId() {
            return store.getters['rapportASignerState/getRapportInterventionFilterInterventionId'];
        },
        filterPredictedDateFrom() {
            return store.getters['rapportASignerState/getRapportInterventionFilterPredictedDateFrom'];
        },
        filterPredictedDateTo() {
            return store.getters['rapportASignerState/getRapportInterventionFilterPredictedDateTo'];
        },
        count() {
            return store.getters['rapportASignerState/getRapportsASignerCount'];
        },
        iconeSignature() {
            return require('@/assets/img/signature2.png');
        },
        isSignerActivated(): boolean {
            return this.listRapportsIdSelected.length === 0;
        },
        isSuccessActive() : boolean {
            return store.getters['successToaster/isActive'];
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportDisabled(): boolean {
            return this.filter.siteList?.length === 0 || this.tableData.length === 0;
        },
    },
    watch: {
        tableData() {
            this.filter.siteList = this.filterSite.length > 0 ? this.filterSite : this.siteList;
        },
        filterSite() {
            this.content?.scrollTo({ top: 0 });
        },
    },
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';
.datatable-container {
    max-height: calc(100vh - 370px);
}

@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 400px);
    }
}

@media screen and (max-width: 1531px) {
    .datatable-container {
        max-height: calc(100vh - 430px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 490px);
    }
}

:deep(.containerBtn) {
    height: 30px;
}
:deep(.icon) {
    height: 20px;
}
.container-header-table{
    height: 40px;
}
.filter-container {
    display: flex;
    padding: 8px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
}
.filter-site-container{
    flex-direction: column;
    width: 15%;
}
.filter {
    width: 100%;
}
.button-confirm {
    right: 16px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 20px;
}
:deep(.filter) {
    margin-bottom: 0;
    padding: 0 16px;
}
</style>
